import React from 'react';
import { Row, Col, Card, Table, Button, Typography, Result, Spin } from 'antd';
import {
  ShopOutlined,
  ClockCircleOutlined,
  CustomerServiceOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import WelcomeBanner from '../../Components/common/WelcomeBanner';
import { ApiErrorState } from '../../Components/common/StateViews';
import StatusTag from '../../Components/common/StatusTag';
import useApiData from '../../Hooks/useApiData';
import cioService from '../../Services/cioService';
import ticketService from '../../Services/ticketService';
import {
  ClientStatusLabels,
  ClientStatusColors,
  TicketStatusLabels,
  TicketStatusColors,
} from '../../Constants/enums';

const GREEN = { color: '#599c16', tint: 'rgba(89,156,22,0.10)' };
const AMBER = { color: '#b54708', tint: 'rgba(247,144,9,0.12)' };
const BLUE = { color: '#175cd3', tint: 'rgba(23,92,211,0.10)' };

const CioDashboard = () => {
  const navigate = useNavigate();
  const dash = useApiData(() => cioService.getDashboard(), []);
  const clients = useApiData(() => cioService.getClients({ pageSize: 6 }), []);
  const tickets = useApiData(() => ticketService.getTickets({ pageSize: 6 }), []);

  const ticketsPending = tickets.error?.status === 404;
  const ticketItems = tickets.data?.items || [];
  // Open-ticket and resolved-today counts come from the CIO dashboard aggregate
  // (CioDashboardResponse.openTickets / resolvedToday), consistent with the other
  // tiles; the "—" fallback only shows if the aggregate omits them.
  const openTickets = dash.data?.openTickets ?? '—';
  const resolvedToday = dash.data?.resolvedToday ?? '—';

  const tiles = [
    { label: 'Clients Onboarded', value: dash.data?.clientsOnboarded ?? 0, icon: <ShopOutlined />, ...GREEN, onClick: () => navigate('/cio/customers') },
    { label: 'Onboarding In Progress', value: dash.data?.onboardingInProgress ?? 0, icon: <ClockCircleOutlined />, ...AMBER, onClick: () => navigate('/cio/customers') },
    { label: 'Open Tickets', value: openTickets, icon: <CustomerServiceOutlined />, ...BLUE, onClick: () => navigate('/cio/support') },
    { label: 'Resolved Today', value: resolvedToday, icon: <CheckCircleOutlined />, ...GREEN, onClick: () => navigate('/cio/support') },
  ];

  const clientColumns = [
    { title: 'Client', dataIndex: 'clientName' },
    { title: 'Account', dataIndex: 'accountNumber' },
    { title: 'Users', key: 'users', render: (_, r) => r.subscription?.seatCount ?? '—' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v) => <StatusTag value={v} labels={ClientStatusLabels} colors={ClientStatusColors} />,
    },
  ];

  const ticketColumns = [
    { title: 'Ticket', dataIndex: 'ticketNumber' },
    { title: 'Client', dataIndex: 'clientName' },
    { title: 'Issue', dataIndex: 'issue', ellipsis: true },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v) => <StatusTag value={v} labels={TicketStatusLabels} colors={TicketStatusColors} />,
    },
  ];

  return (
    <div>
      <WelcomeBanner icon={<ShopOutlined />} />

      {dash.error ? (
        <ApiErrorState error={dash.error} onRetry={dash.reload} />
      ) : (
        <div className="stat-grid">
          {tiles.map((t) => (
            <div key={t.label} className="stat-tile clickable" onClick={t.onClick}>
              <div className="icon" style={{ background: t.tint, color: t.color }}>{t.icon}</div>
              <div className="label">{t.label}</div>
              <div className="value">{t.value}</div>
            </div>
          ))}
        </div>
      )}

      <Row gutter={[16, 16]} align="stretch" style={{ marginTop: 16 }}>
        <Col xs={24} xl={12}>
          <Card
            title="Client Onboarding"
            size="small"
            style={{ height: '100%' }}
            extra={
              <Button size="small" icon={<MailOutlined />} onClick={() => navigate('/cio/invitations')}>
                Send invitation
              </Button>
            }
          >
            <Table
              rowKey="id"
              size="small"
              loading={clients.loading}
              columns={clientColumns}
              dataSource={clients.data?.items || []}
              pagination={false}
              onRow={(r) => ({ onClick: () => navigate(`/cio/customers/${r.id}`), style: { cursor: 'pointer' } })}
            />
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
              Set client details, users, and plan. Once a plan is chosen, billing is handed to the CFO office to issue the invoice.
            </Typography.Text>
          </Card>
        </Col>

        <Col xs={24} xl={12}>
          <Card
            title="Tech Support & Issues"
            size="small"
            style={{ height: '100%' }}
            extra={
              <Button size="small" icon={<PlusOutlined />} onClick={() => navigate('/cio/support')}>
                New ticket
              </Button>
            }
          >
            {ticketsPending ? (
              <Result
                style={{ padding: '24px 8px' }}
                icon={<CustomerServiceOutlined style={{ color: '#599c16' }} />}
                title="Awaiting the ticket backend"
                subTitle="Built to Sai's spec; will populate when the ticket API is connected."
              />
            ) : tickets.loading ? (
              <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
            ) : (
              <Table
                rowKey="id"
                size="small"
                columns={ticketColumns}
                dataSource={ticketItems}
                pagination={false}
                onRow={() => ({ onClick: () => navigate('/cio/support'), style: { cursor: 'pointer' } })}
              />
            )}
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
              Each ticket stores client, account, time occurred, and issue. The client follows the status on their dashboard.
            </Typography.Text>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CioDashboard;
